import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  ShieldIcon,
  CheckCircleIcon,
  PhoneIcon,
  SmsIcon,
  CheckIcon,
  UserCircleIcon,
  FacebookIcon,
  GoogleIcon,
  LinkedinIcon
} from '../../utils/IconWrapper';
import { 
  sendPhoneVerificationCode, 
  verifyPhoneCode, 
  updateUserProfile 
} from '../../utils/supabaseExtended';

const StepContainer = styled.div`
  padding: 1rem 0;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  color: #0d9488;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StepDescription = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 2rem;
`;

const SecuritySection = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  border: 2px solid var(--border-color);
`;

const SectionTitle = styled.h3`
  color: #0d9488;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PhoneVerificationCard = styled.div<{ verified?: boolean }>`
  background: ${props => props.verified ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-secondary)'};
  border: 2px solid ${props => props.verified ? '#10b981' : 'var(--border-color)'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
`;

const VerificationStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const StatusIcon = styled.div<{ verified?: boolean }>`
  color: ${props => props.verified ? '#10b981' : 'var(--text-secondary)'};
  font-size: 1.2rem;
`;

const StatusText = styled.span<{ verified?: boolean }>`
  color: ${props => props.verified ? '#10b981' : 'var(--text-primary)'};
  font-weight: 500;
`;

const VerificationInput = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin: 1rem 0;
`;

const CodeInput = styled.input`
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  text-align: center;
  letter-spacing: 0.1em;
  width: 150px;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: #0d9488;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary'; loading?: boolean; disabled?: boolean }>`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(13, 148, 136, 0.4);
    }
  ` : `
    background: var(--bg-primary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover:not(:disabled) {
      background: var(--bg-secondary);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProfileSection = styled.div`
  margin: 1.5rem 0;
`;

const ProfileInput = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: #0d9488;
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const SocialVerification = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const SocialButton = styled.button<{ connected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.connected ? '#10b981' : 'var(--border-color)'};
  background: ${props => props.connected ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-secondary)'};
  color: ${props => props.connected ? '#10b981' : 'var(--text-primary)'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #0d9488;
  }
`;

const CompletionCard = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  margin: 2rem 0;
  border: 2px solid #10b981;
`;

const CompletionIcon = styled.div`
  font-size: 3rem;
  color: #10b981;
  margin-bottom: 1rem;
`;

const CompletionTitle = styled.h3`
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const CompletionText = styled.p`
  color: #047857;
  margin-bottom: 1.5rem;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const InfoText = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0.5rem 0;
`;

interface RegistrationStep3Props {
  onNext: (data: any) => void;
  onPrevious: () => void;
  initialData?: any;
}

const RegistrationStep3: React.FC<RegistrationStep3Props> = ({
  onNext,
  onPrevious,
  initialData
}) => {
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [bio, setBio] = useState(initialData?.bio || '');
  const [socialConnections, setSocialConnections] = useState({
    facebook: false,
    google: false,
    linkedin: false
  });

  const handleSendVerificationCode = async () => {
    setIsLoading('sendCode');
    
    try {
      const result = await sendPhoneVerificationCode();
      
      if (result.success) {
        setPhoneCodeSent(true);
        alert('Codice inviato via SMS!');
      } else {
        alert(result.error || 'Errore nell\'invio del codice');
      }
    } catch (error) {
      alert('Errore di connessione');
    } finally {
      setIsLoading(null);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      alert('Inserisci un codice valido a 6 cifre');
      return;
    }

    setIsLoading('verifyCode');
    
    try {
      const result = await verifyPhoneCode(verificationCode);
      
      if (result.success) {
        setPhoneVerified(true);
        alert('Telefono verificato con successo!');
      } else {
        alert(result.error || 'Codice non valido');
      }
    } catch (error) {
      alert('Errore di verifica');
    } finally {
      setIsLoading(null);
    }
  };

  const handleSocialConnect = (platform: string) => {
    // Qui implementeresti l'integrazione OAuth reale
    setSocialConnections(prev => ({
      ...prev,
      [platform]: !prev[platform as keyof typeof prev]
    }));
  };

  const handleComplete = async () => {
    setIsLoading('complete');
    
    try {
      // Aggiorna il profilo con bio e fase 3 completata
      await updateUserProfile({
        bio,
        registration_phase: 3,
        facebook_verified: socialConnections.facebook,
        google_verified: socialConnections.google,
        linkedin_verified: socialConnections.linkedin
      });

      const completionData = {
        bio,
        phoneVerified,
        socialConnections,
        registrationPhase: 3
      };

      onNext(completionData);
    } catch (error) {
      alert('Errore nel completamento della registrazione');
    } finally {
      setIsLoading(null);
    }
  };

  const isReadyToComplete = phoneVerified; // Requisito minimo

  return (
    <StepContainer>
      <StepTitle>Sicurezza e personalizzazione</StepTitle>
      <StepDescription>
        Ultimo passo: aggiungi sicurezza al tuo account e personalizza il tuo profilo
      </StepDescription>

      {/* Verifica telefono */}
      <SecuritySection>
        <SectionTitle>
          <ShieldIcon />
          Verifica del telefono
        </SectionTitle>
        
        <PhoneVerificationCard verified={phoneVerified}>
          <VerificationStatus>
            <StatusIcon verified={phoneVerified}>
              {phoneVerified ? <CheckCircleIcon /> : <PhoneIcon />}
            </StatusIcon>
            <StatusText verified={phoneVerified}>
              {phoneVerified ? 'Telefono verificato' : 'Verifica il tuo numero di telefono'}
            </StatusText>
          </VerificationStatus>

          {!phoneVerified && (
            <>
              <InfoText>
                Ti invieremo un codice a 6 cifre via SMS per verificare il tuo numero
              </InfoText>
              
              {!phoneCodeSent ? (
                <Button 
                  variant="primary" 
                  onClick={handleSendVerificationCode}
                  disabled={isLoading === 'sendCode'}
                  loading={isLoading === 'sendCode'}
                >
                  {isLoading === 'sendCode' && <LoadingSpinner />}
                  <SmsIcon />
                  Invia codice SMS
                </Button>
              ) : (
                <VerificationInput>
                  <CodeInput
                    type="text"
                    maxLength={6}
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <Button 
                    variant="primary" 
                    onClick={handleVerifyCode}
                    disabled={isLoading === 'verifyCode' || verificationCode.length !== 6}
                    loading={isLoading === 'verifyCode'}
                  >
                    {isLoading === 'verifyCode' && <LoadingSpinner />}
                    <CheckIcon />
                    Verifica
                  </Button>
                </VerificationInput>
              )}
            </>
          )}
        </PhoneVerificationCard>
      </SecuritySection>

      {/* Profilo pubblico */}
      <ProfileSection>
        <SectionTitle>
          <UserCircleIcon />
          Profilo pubblico (opzionale)
        </SectionTitle>
        
        <InfoText>
          Aggiungi una breve descrizione di te per i tuoi futuri host
        </InfoText>
        
        <ProfileInput
          placeholder="Ciao! Sono Marco, un appassionato di viaggi che ama scoprire nuovi luoghi e culture. Sono una persona rispettosa e pulita, e mi piace interagire con i locali per vivere esperienze autentiche..."
          value={bio}
          onChange={(e) => setBio(e.target.value)}
        />
      </ProfileSection>

      {/* Verifica social */}
      <SecuritySection>
        <SectionTitle>
          <ShieldIcon />
          Verifica social (opzionale)
        </SectionTitle>
        
        <InfoText>
          Collega i tuoi account social per aumentare la fiducia nella community
        </InfoText>
        
        <SocialVerification>
          <SocialButton 
            connected={socialConnections.facebook}
            onClick={() => handleSocialConnect('facebook')}
          >
            <FacebookIcon />
            Facebook
            {socialConnections.facebook && <CheckIcon />}
          </SocialButton>
          
          <SocialButton 
            connected={socialConnections.google}
            onClick={() => handleSocialConnect('google')}
          >
            <GoogleIcon />
            Google
            {socialConnections.google && <CheckIcon />}
          </SocialButton>
          
          <SocialButton 
            connected={socialConnections.linkedin}
            onClick={() => handleSocialConnect('linkedin')}
          >
            <LinkedinIcon />
            LinkedIn
            {socialConnections.linkedin && <CheckIcon />}
          </SocialButton>
        </SocialVerification>
      </SecuritySection>

      {/* Completamento */}
      {isReadyToComplete && (
        <CompletionCard>
          <CompletionIcon>
            <CheckCircleIcon />
          </CompletionIcon>
          <CompletionTitle>Perfetto! Sei pronto per iniziare</CompletionTitle>
          <CompletionText>
            Il tuo account è quasi pronto. Clicca "Completa registrazione" per iniziare a prenotare i nostri incredibili alloggi e ricevere sconti esclusivi!
          </CompletionText>
          
          <Button 
            variant="primary" 
            onClick={handleComplete}
            disabled={isLoading === 'complete'}
            loading={isLoading === 'complete'}
            style={{ fontSize: '1.1rem', padding: '1rem 2rem' }}
          >
            {isLoading === 'complete' && <LoadingSpinner />}
            🎉 Completa registrazione
          </Button>
        </CompletionCard>
      )}

      <NavigationButtons>
        <Button variant="secondary" onClick={onPrevious}>
          Indietro
        </Button>
        
        {!isReadyToComplete && (
          <Button 
            variant="primary" 
            onClick={() => alert('Completa la verifica del telefono per continuare')}
            disabled
          >
            Completa verifica telefono
          </Button>
        )}
      </NavigationButtons>
    </StepContainer>
  );
};

export default RegistrationStep3; 